import React, { useEffect, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/auth';
import PrivateRoute from './auth/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { initAnalytics } from './utils/analytics';
import RouteTracker from './utils/RouteTracker';

import Mainframe from './components/Search/Mainframe/Mainframe';
import NotFound from './components/Syn/NotFound/NotFound';

const SearchResults = lazy(() => import('./components/Search/Searched/Searched'));
const Support = lazy(() => import('./components/Syn/Support/Support'));
const AboutUs = lazy(() => import('./components/Syn/AboutUs/AboutUs'));
const Terms = lazy(() => import('./components/Syn/Terms/Terms'));
const Privacy = lazy(() => import('./components/Syn/Privacy/Privacy'));
const Contact = lazy(() => import('./components/Syn/Contact/Contact'));
const WhatIsSpiggl = lazy(() => import('./components/Syn/WhatIsSpiggl/WhatIsSpiggl'));
const ForgotPassword = lazy(() => import('./auth/ForgotPassword/ForgotPassword'));
const CreateProfile = lazy(() => import('./components/CreateProfile/CreateProfile'));
const UserProfile = lazy(() => import('./components/User/UserProfile'));
const CompanyProfile = lazy(() => import('./components/Company/CompanyProfile'));
const Schedule = lazy(() => import('./components/Schedule/Schedule'));
const EditProfileUser = lazy(() => import('./components/Settings/Profile/EditProfileUser'));
const EditProfileCompany = lazy(() => import('./components/Settings/Profile/EditProfileCompany'));
const PostHighlight = lazy(() => import('./components/Post/PostHighlight/PostHighlight'));
const PostReview = lazy(() => import('./components/Post/PostReview/PostReview'));
const UserSpigglPayScreen = lazy(() => import('./components/Transaction/SpigglPayScreen/UserSpigglPayScreen'));
const Payment = lazy(() => import('./components/Transaction/Payment'));
const UserSpigglPayZoom = lazy(() => import('./components/Transaction/SpigglPayZoom/UserSpigglPayZoom'));
const CompanySpigglPayScreen = lazy(() => import('./components/Transaction/SpigglPayScreen/CompanySpigglPayScreen'));
const CompanySpigglPayZoom = lazy(() => import('./components/Transaction/SpigglPayZoom/CompanySpigglPayZoom'));
const SendInvoice = lazy(() => import('./components/Transaction/SendInvoice/SendInvoice'));
const Bank = lazy(() => import('./components/Transaction/LinkBank/LinkBank'));
const UpdateBank = lazy(() => import('./components/Transaction/UpdateLinkBank/UpdateLinkBank'));
const StripeReturn = lazy(() => import('./components/Transaction/LinkBank/StripeReturn'));
const StripeRefresh = lazy(() => import('./components/Transaction/LinkBank/StripeRefresh'));
const MessagesPage = lazy(() => import('./components/Messages/MessagesPage'));

const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--color-background, #F7F9FC)',
  }}>
    <div style={{
      width: '32px',
      height: '32px',
      border: '3px solid #e2e8f0',
      borderTopColor: '#333',
      borderRadius: '50%',
      animation: 'spin 0.6s linear infinite',
    }} />
  </div>
);

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <ErrorBoundary>
    <Router>
      <AuthProvider>
        <RouteTracker />
        <div className="App">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Mainframe />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/search-results/:type/:zip" element={<SearchResults />} />
              <Route path="/create-profile" element={<CreateProfile />} />
              <Route path="/user/:user" element={<UserProfile />} />
              <Route path="/contractor/:companyUser" element={<CompanyProfile />} />
              <Route path="/contractor/:companyUser/calendar" element={<Schedule />} />
              <Route path="/support" element={<Support />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/what-is-spiggl" element={<WhatIsSpiggl />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/post-highlight" element={<PrivateRoute owner="company"><PostHighlight /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/management" element={<PrivateRoute owner="company"><EditProfileCompany /></PrivateRoute>} />
              <Route path="/user/:user/post-rating" element={<PrivateRoute owner="user"><PostReview /></PrivateRoute>} />
              <Route path="/user/:user/management" element={<PrivateRoute owner="user"><EditProfileUser /></PrivateRoute>} />
              <Route path="/user/:user/spiggl-pay" element={<PrivateRoute owner="user"><UserSpigglPayScreen /></PrivateRoute>} />
              <Route path="/user/:user/spiggl-pay/invoice/:invoiceID" element={<PrivateRoute owner="user"><UserSpigglPayZoom /></PrivateRoute>} />
              <Route path="/user/:user/spiggl-pay/card" element={<PrivateRoute owner="user"><Payment /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/spiggl-pay" element={<PrivateRoute owner="company"><CompanySpigglPayScreen /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/spiggl-pay/link-bank" element={<PrivateRoute owner="company"><Bank /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/spiggl-pay/update-bank" element={<PrivateRoute owner="company"><UpdateBank /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/spiggl-pay/stripe-return" element={<PrivateRoute owner="company"><StripeReturn /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/spiggl-pay/stripe-refresh" element={<PrivateRoute owner="company"><StripeRefresh /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/spiggl-pay/invoice/:invoiceID" element={<PrivateRoute owner="company"><CompanySpigglPayZoom /></PrivateRoute>} />
              <Route path="/contractor/:companyUser/spiggl-pay/new-invoice" element={<PrivateRoute owner="company"><SendInvoice /></PrivateRoute>} />
            </Routes>
          </Suspense>
        </div>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
